import React from 'react';
import PropTypes from 'prop-types';
import {bindActionCreators} from 'redux';
import {connect} from 'react-redux';
import {Translate} from 'react-redux-i18n';
import {Route, Switch, withRouter} from 'react-router-dom';
import moment from 'moment-timezone';

import {SidePanel, UploadProjectSharedDocument} from 'front-onceforall-core';
import {DOSSIER_STATE} from 'front-onceforall-core/dist/utils/dossiers';

import config from '../../../config';

import {addError, addSuccess} from '../../actions/snackbars';
import {PROJECT_STATUS, status} from '../../utils/constant';
import {captureError} from '../../utils/log';
import {cancelInterventionInvitation} from '../../utils/relationship';
import {deepCopy} from '../../utils/objects';
import projectService from '../../services/project/ProjectService';
import interventionService from '../../services/intervention/InterventionService';
import documentService from '../../services/document/DocumentService';
import documentKpiService from '../../services/documentKpi/DocumentKpiService';
import enrollmentService from '../../services/enrollment';
import ProjectHeader from '../../components/project/ProjectHeader';
import ProjectTree from '../../components/project/projectTree/ProjectTree';
import ProjectEditView from '../projectEdit/ProjectEditView';
import PageNotFound from '../../components/pageNotFound';
import ProjectDashboard from '../../components/project/ProjectDashboard';
import DownloadAndUploadProjectSharedDocumentPanel from '../../components/document/UploadPanel/DownloadAndUploadProjectSharedDocumentPanel';
import GrantProjectAccess from '../../components/grantProjectAccess';
import ResendReasonForm from '../../components/notification/ResendReasonForm';
import InterventionAdd from '../../components/interventionAdd';
import InvalidOrganisationDocuments from '../../components/invalidOrganisationDocuments';
import ProjectRequiredDocuments from '../../components/ProjectRequiredDocuments';
import ErrorOverlay from '../../components/ErrorBoundary/ErrorOverlay';

const pathDepth = intervention => (intervention.interventionPath.match(/\//g) || []).length;
const DISPLAYED_STATUS = [status.ACCEPTED, status.PENDING, status.ENDED, status.REJECTED];
const SUBPAGES = ['chart', 'invalid-organisation-documents', 'required-documents', 'user-access'];

export const ProjectDossierStatsContext = React.createContext({});

export class ProjectDetailsView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      document: null,
      error: false,
      loading: true,
      project: {},
      myIntervention: {},
      interventions: [],
      currentInvitationToResend: null,
      requiredDocumentTypes: [],
      isResendReasonPanelOpen: false,
      isOrganisationsPanelOpen: false,
      isEditProjectPanelOpen: false,
      projectSharedDocuments: [],
      loadingProjectSharedDocuments: true,
      isProjectSharedDocumentPanelOpen: false,
      isProjectSharedDocumentUploadPanelOpen: false,
      isProjectSharedDocumentTemplateUploadPanelOpen: false,
      isSubmittingProjectSharedDocument: false,
      stats: {
        validProjectDossierStates: {
          loading: true,
          error: null,
          percentage: 0
        },
        projectDossierStatesDocuments: {
          loading: true,
          error: null,
          percentage: {}
        },
        participants: {
          loading: true,
          count: null
        }
      }
    };

    this.handleRefresh = this.handleRefresh.bind(this);
    this.handleOpenEditProjectPanel = this.handleOpenEditProjectPanel.bind(this);
    this.handleOpenOrganisationsPanel = this.handleOpenOrganisationsPanel.bind(this);
    this.handleCloseSidePanels = this.handleCloseSidePanels.bind(this);
    this.handleUploadSuccess = this.handleUploadSuccess.bind(this);
    this.handleResendInvitation = this.handleResendInvitation.bind(this);
    this.resendInvitation = this.resendInvitation.bind(this);
    this.handleSubmitResendReason = this.handleSubmitResendReason.bind(this);
    this.handleCloseResendReasonPanel = this.handleCloseResendReasonPanel.bind(this);
    this.updateSentInvitations = this.updateSentInvitations.bind(this);
    this.handleCloseProject = this.handleCloseProject.bind(this);
    this.handleEndOrganisation = this.handleEndOrganisation.bind(this);
    this.handleCancelIntervention = this.handleCancelIntervention.bind(this);
    this.handleRemoveOrganisation = this.handleRemoveOrganisation.bind(this);
    this.fetchProjectSharedDocuments = this.fetchProjectSharedDocuments.bind(this);
    this.handleAddProjectSharedDocument = this.handleAddProjectSharedDocument.bind(this);
    this.handleUploadProjectSharedDocument = this.handleUploadProjectSharedDocument.bind(this);
    this.handleSubmitProjectSharedDocument = this.handleSubmitProjectSharedDocument.bind(this);
    this.handleRequireProjectSpecificDocument = this.handleRequireProjectSpecificDocument.bind(this);
  }

  componentDidMount() {
    this.handleRefresh();
  }

  handleRequireProjectSpecificDocument(subtype) {
    this.setState({loadingProjectSharedDocuments: true});
    documentService.requireProjectSpecificDocument(this.props.projectId, subtype, this.props.organisation.organisationId)
      .then(this.fetchProjectSharedDocuments)
      .catch(() => {
        this.props.actions.addError(<Translate value="error.generic"/>);
        this.setState({loadingProjectSharedDocuments: false});
      });
  }

  handleSubmitResendReason(reason) {
    const invitation = this.state.currentInvitationToResend;
    this.resendInvitation(enrollmentService.project, invitation.invitationId, invitation.organisation.fullName, reason);
    this.handleCloseResendReasonPanel();
  }

  resendInvitation(service, invitationId, invitedOrganisationName, reason = {}) {
    this.setState({loading: true});
    service.resendInvitation(invitationId, reason)
      .then(response => {
        this.props.actions.addSuccess(<Translate
          value="subcontractor.invite.resendSuccess"
          name={invitedOrganisationName}
        />);
        this.updateSentInvitations(response);
      })
      .catch(() => this.props.actions.addError(<Translate value="error.generic"/>))
      .finally(() => this.setState({loading: false}));
  }

  handleCloseResendReasonPanel() {
    this.setState({
      isResendReasonPanelOpen: false,
      currentInvitationToResend: null
    });
  }

  handleResendInvitation(invitation) {
    const invitationId = invitation.interventionId ? invitation.interventionId : invitation.invitationId;
    if (invitationId !== undefined) {
      if (invitation.interventionId) {
        this.resendInvitation(interventionService, invitationId, invitation.organisation.fullName);
      } else {
        this.setState({
          isResendReasonPanelOpen: true,
          currentInvitationToResend: invitation
        });
      }
    }
  }

  handleRemoveOrganisation(intervention) {
    interventionService.removeOrganisationParticipation(intervention.interventionId)
      .then(() => {
        this.props.actions.addSuccess(<Translate
          value="intervention.removed.removeSuccess"
          organisationName={intervention.organisation.fullName}
        />);
        if (intervention.organisation.organisationId === this.props.organisation.organisationId) {
          this.props.history.push('/projects');
        } else {
          this.fetchAll();
        }
      })
      .catch(() => this.props.actions.addError(<Translate value="error.generic"/>));
  }

  handleEndOrganisation(intervention) {
    interventionService.endOrganisationParticipation(intervention.interventionId)
      .then(() => {
        this.props.actions.addSuccess(<Translate
          value="intervention.ended.endSuccess"
          organisationName={intervention.organisation.fullName}
        />);
        this.fetchAll();
      })
      .catch(() => this.props.actions.addError(<Translate value="error.generic"/>));
  }

  handleCancelIntervention(intervention) {
    cancelInterventionInvitation(intervention)
      .then(() => {
        this.props.actions.addSuccess(<Translate value="invite.cancelSuccessful"/>);
        this.fetchAll();
      }).catch(error => {
        captureError('Error when canceling the intervention', error);
        this.props.actions.addError(<Translate value="error.generic"/>);
      });
  }

  updateSentInvitations(response) {
    this.setState(state => {
      const interventions = JSON.parse(JSON.stringify(state.interventions));
      const interventionIndex = interventions
        .findIndex(intervention => (intervention.interventionId !== null &&
          intervention.interventionId === response.interventionId) ||
          (intervention.invitationId !== null &&
          intervention.invitationId === response.invitationId));

      interventions[interventionIndex] = {
        ...interventions[interventionIndex],
        resendable: response.resendable,
        creationDate: response.creationDate,
        status: response.status,
        rejectionReason: response.rejectionReason,
        lastSentDate: response.lastSentDate
      };
      return {interventions};
    });
  }

  handleRefresh() {
    this.fetchAll();
    this.fetchProjectSharedDocuments();
    if (config.featureFlags.showProjectKPIs) {
      this.fetchValidProjectDossierStatesPercentage();
      this.fetchProjectDossierStatesPercentageByDocumentState();
    }
  }

  fetchValidProjectDossierStatesPercentage = (interventionId = this.props.interventionId) => {
    this.setState(state => ({
      stats: {
        ...state.stats,
        validProjectDossierStates: {
          ...state.stats.validProjectDossierStates,
          loading: true,
          error: null
        }
      }
    }));

    documentKpiService.getValidProjectDossierStatesPercentageByIntervention(this.props.organisation.organisationId, interventionId)
      .then(percentageResponse => {
        this.setState(state => ({
          stats: {
            ...state.stats,
            validProjectDossierStates: {
              percentage: percentageResponse,
              loading: false,
              error: null
            }
          }
        }));
      })
      .catch(error => {
        let errorMessage = 'message.noData';
        if (!error.response || error.response.status !== 404) {
          captureError('documentKpiService.getValidProjectDossierStatesPercentageByIntervention', error);
          errorMessage = 'error.generic';
        }

        this.setState(state => ({
          stats: {
            ...state.stats,
            validProjectDossierStates: {
              ...state.stats.validProjectDossierStates,
              loading: false,
              error: errorMessage
            }
          }
        }));
      });
  };

  fetchProjectDossierStatesPercentageByDocumentState = (interventionId = this.props.interventionId) => {
    this.setState(state => ({
      stats: {
        ...state.stats,
        projectDossierStatesDocuments: {
          ...state.stats.projectDossierStatesDocuments,
          loading: true,
          error: null
        }
      }
    }));

    documentKpiService.getProjectDocumentsPercentageByDocumentStateByIntervention(this.props.organisation.organisationId, interventionId)
      .then(percentageResponse => {
        this.setState(state => ({
          stats: {
            ...state.stats,
            projectDossierStatesDocuments: {
              percentage: percentageResponse,
              loading: false,
              error: null
            }
          }
        }));
      })
      .catch(error => {
        let errorMessage = 'message.noData';
        if (!error.response || error.response.status !== 404) {
          captureError('documentKpiService.getProjectDocumentsPercentageByDocumentStateByIntervention', error);
          errorMessage = 'error.generic';
        }

        this.setState(state => ({
          stats: {
            ...state.stats,
            projectDossierStatesDocuments: {
              ...state.stats.projectDossierStatesDocuments,
              loading: false,
              error: errorMessage
            }
          }
        }));
      });
  };

  fetchAll = () => {
    const {projectId, interventionId} = this.props;
    this.setState(state => ({
      loading: true,
      stats: {...state.stats, participants: {...state.participants, loading: true}}
    }));
    Promise.all([
      projectService.findOne(projectId),
      interventionService.getInterventionTree(interventionId)
    ])
      .then(async ([project, interventionsResponse]) => {
        let interventions = interventionsResponse.map(intervention => ({depth: pathDepth(intervention), ...intervention}));
        interventions = await this.fetchDossierStates(interventions);
        interventions = await this.fetchInvitations(projectId, interventions);

        const myIntervention = interventions.find(intervention => intervention.interventionId === interventionId);

        const count = interventionsResponse.filter(intervention => intervention.dossierDocumentsAccessible).length;
        this.setState(state => ({
          project,
          myIntervention,
          interventions,
          loading: false,
          error: false,
          stats: {...state.stats, participants: {count, loading: false}}
        }));
      })
      .catch(() => {
        this.setState({
          error: true,
          loading: false
        });
      });
  };

  fetchInvitations(projectId, interventions) {
    return enrollmentService.project.searchBy({
      projectId,
      status: [status.PENDING],
      organisationId: this.props.organisation.organisationId
    })
      .then(invitations => {
        const result = deepCopy(interventions);
        invitations.forEach(invitation => {
          if (invitation.clone) {
            const index = result.findIndex(el => el.invitationId === invitation.invitationId);
            result[index].invitation = invitation;
          }
        });
        return result;
      });
  }

  fetchDossierStates(interventions) {
    const promises = interventions.map(async intervention => {
      if (intervention.dossierStateAccessible) {
        if (!intervention.organisation.depositor) {
          return {...intervention, dossierState: DOSSIER_STATE.DEPOSITOR_SERVICE_DEACTIVATED};
        }

        const aborter = new AbortController();

        try {
          const {dossierState} = await documentService.getInterventionDossierState(intervention.interventionId, aborter.signal);
          return {...intervention, dossierState};
        } catch (error) {
          return {...intervention, dossierState: 'UNKNOWN'};
        }
      } else {
        return {...intervention, dossierState: 'NULL'};
      }
    });

    return Promise.all(promises);
  }

  fetchProjectSharedDocuments() {
    this.setState({loadingProjectSharedDocuments: true});
    documentService.getProjectSharedDocuments(this.props.projectId, this.props.organisation.organisationId)
      .then(projectSharedDocuments => this.setState({projectSharedDocuments, loadingProjectSharedDocuments: false}))
      .catch(() => this.setState({projectSharedDocuments: [], loadingProjectSharedDocuments: false}));
  }

  handleAddProjectSharedDocument() {
    this.setState({isProjectSharedDocumentPanelOpen: true, document: null});
  }

  handleUploadProjectSharedDocument(document) {
    if (document.type.code === 'CDC_NOMINATIF') {
      this.setState({isProjectSharedDocumentTemplateUploadPanelOpen: true, document});
    } else {
      this.setState({isProjectSharedDocumentPanelOpen: true, document});
    }
  }

  handleSubmitProjectSharedDocument(file, info) {
    if (this.state.isSubmittingProjectSharedDocument) {
      return;
    }

    this.setState({isSubmittingProjectSharedDocument: true});

    documentService.submitProjectSharedDocument(this.props.projectId, file, info, this.state.document)
      .then(() => {
        this.fetchProjectSharedDocuments();
        this.props.actions.addSuccess(<Translate value="document.uploadSuccess"/>);
      })
      .catch(() => {
        this.props.actions.addError(<Translate value="error.generic"/>);
      })
      .finally(() => {
        this.setState({
          isSubmittingProjectSharedDocument: false,
          isProjectSharedDocumentPanelOpen: false,
          isProjectSharedDocumentTemplateUploadPanelOpen: false
        });
      });
  }

  isProjectClosed() {
    return this.state.project?.projectStatus === PROJECT_STATUS.CLOSED;
  }

  handleOpenEditProjectPanel() {
    if (!this.isProjectClosed()) {
      this.setState({isEditProjectPanelOpen: true});
    }
  }

  handleOpenOrganisationsPanel() {
    if (!this.isProjectClosed()) {
      this.setState({isOrganisationsPanelOpen: true});
    }
  }

  goBackToDashboard = () => {
    this.handleRefresh();
    this.props.history.push(`/project/${this.props.projectId}/intervention/${this.props.interventionId}`);
  };

  handleCloseSidePanels() {
    this.handleRefresh();
    this.setState({
      isEditProjectPanelOpen: false,
      isOrganisationsPanelOpen: false,
      isProjectSharedDocumentPanelOpen: false,
      isProjectSharedDocumentUploadPanelOpen: false,
      isProjectSharedDocumentTemplateUploadPanelOpen: false
    });
  }

  handleUploadSuccess() {
    this.setState({
      isProjectSharedDocumentUploadPanelOpen: false,
      isProjectSharedDocumentTemplateUploadPanelOpen: false
    });
    this.refreshDocuments();
  }

  handleCloseProject() {
    projectService.closeProject(this.props.projectId)
      .then(response => {
        this.setState(state => ({
          project: {
            ...state.project,
            projectStatus: response.projectStatus,
            closingDate: response.closingDate}
        }));
        this.props.actions.addSuccess(<Translate value="project.closeSuccess"/>);
      })
      .then(this.fetchAll)
      .catch(() => {
        this.props.actions.addError(<Translate value="error.generic"/>);
      });
  }

  handleGetInvalidDocuments = (page, pageSize) => documentKpiService.getInvalidOrganisationDocumentsByIntervention(
    this.props.interventionId,
    this.props.organisation.organisationId,
    page,
    pageSize
  );

  render() {
    if (this.state.loading) {
      return (<span className="loading-overlay"/>);
    }

    if (this.state.error) {
      return <ErrorOverlay/>;
    }

    const filteredInterventions = this.state.interventions.filter(
      intervention => DISPLAYED_STATUS.includes(intervention.status)
    );

    const isSubPage = SUBPAGES.includes(this.props.location.pathname.split('/').pop());
    const backButtonLink = isSubPage ? `/project/${this.props.projectId}/intervention/${this.props.interventionId}` : '/projects';
    const backButtonLabel = isSubPage ? 'project.backToDashboard' : 'project.backToProjects';
    const showAdditionalDesignation = !this.state.document || (this.state.document.type && this.state.document.type.code.startsWith(
      'GENERIC_PROJECT_SHARED_'
    ));

    return (
      <div>
        <ProjectHeader
          project={this.state.project}
          organisationId={this.props.organisation.organisationId}
          myIntervention={this.state.myIntervention}
          onOpenOrganisationsPanel={this.handleOpenOrganisationsPanel}
          onOpenEditProjectPanel={this.handleOpenEditProjectPanel}
          onSubmitCloseProject={this.handleCloseProject}
          backButtonLink={backButtonLink}
          backButtonLabel={backButtonLabel}
          currentLocale={this.props.currentLocale}
          user={this.props.user}
          actions={this.props.actions}
        />

        <Switch>
          <Route
            exact
            path="/project/:projectId/intervention/:interventionId"
            render={() => <ProjectDossierStatsContext.Provider value={this.state.stats}>
              <ProjectDashboard
                interventions={filteredInterventions}
                project={this.state.project}
                myIntervention={this.state.myIntervention}
                organisationId={this.props.organisation.organisationId}
                projectSharedDocuments={this.state.projectSharedDocuments}
                onAddProjectSharedDocument={this.handleAddProjectSharedDocument}
                onUploadProjectSharedDocument={this.handleUploadProjectSharedDocument}
                onRequireProjectSpecificDocument={this.handleRequireProjectSpecificDocument}
                loadingProjectSharedDocuments={this.state.loadingProjectSharedDocuments}
                onRefresh={this.handleRefresh}
              />
            </ProjectDossierStatsContext.Provider>
            }
          />
          <Route
            exact
            path="/project/:projectId/intervention/:interventionId/chart"
            render={() => <ProjectTree
              project={this.state.project}
              interventions={filteredInterventions}
              onResendInvitation={this.handleResendInvitation}
              onRemoveOrganisation={this.handleRemoveOrganisation}
              onSubmitEndOrganisation={this.handleEndOrganisation}
              onCancelIntervention={this.handleCancelIntervention}
              myIntervention={this.state.myIntervention}
              onRefresh={this.handleRefresh}
            />
            }
          />
          <Route
            exact
            path="/project/:projectId/intervention/:interventionId/invalid-organisation-documents"
            render={() => <InvalidOrganisationDocuments
              country={this.state.project.country}
              interventions={this.state.interventions}
              onGetDocuments={this.handleGetInvalidDocuments}
              title={<Translate value="invalidOrganisationDocuments.title"/>}
            />
            }
          />
          <Route
            exact
            path="/project/:projectId/intervention/:interventionId/required-documents"
            render={() => <ProjectRequiredDocuments
              project={this.state.project}
              onClose={this.goBackToDashboard}
            />
            }
          />
          <Route
            exact
            path="/project/:projectId/intervention/:interventionId/user-access"
            render={() => <GrantProjectAccess
              project={this.state.project}
              interventionId={this.props.interventionId}
              onClose={this.goBackToDashboard}
            />
            }
          />
          <Route component={PageNotFound}/>
        </Switch>

        {this.state.isEditProjectPanelOpen &&
          <SidePanel
            big
            onClose={this.handleCloseSidePanels}
          >
            <ProjectEditView
              project={this.state.project}
              onClose={this.handleCloseSidePanels}
              oldestInterventionDateSet={this.state.interventions
                .reduce((previousDate, intervention) => {
                  const currentDate = intervention?.interventionDate;
                  if (!currentDate) { return previousDate; }
                  if (!previousDate) { return currentDate; }
                  return moment(previousDate).isBefore(moment(currentDate)) ? previousDate : currentDate;
                }, null)
              }
            />
          </SidePanel>
        }

        {this.state.isOrganisationsPanelOpen &&
          <SidePanel
            setCloseTrigger={closeFunction => {
              this.triggerCloseAddInterventionForm = closeFunction;
            }}
            onClose={this.handleCloseSidePanels}
            big
          >
            <InterventionAdd
              interventions={this.state.interventions}
              project={this.state.project}
              myIntervention={this.state.myIntervention}
              onClose={() => this.triggerCloseAddInterventionForm()}
            />
          </SidePanel>
        }

        {this.state.isProjectSharedDocumentPanelOpen &&
          <SidePanel onClose={this.handleCloseSidePanels}>
            <UploadProjectSharedDocument
              onCancel={this.handleCloseSidePanels}
              onHandleSubmitFile={this.handleSubmitProjectSharedDocument}
              submitted={this.state.isSubmittingProjectSharedDocument}
              showAdditionalDesignation={showAdditionalDesignation}
              extensions={'.pdf,.docx,.xlsx'}
            />
          </SidePanel>
        }

        {this.state.isProjectSharedDocumentTemplateUploadPanelOpen &&
          <DownloadAndUploadProjectSharedDocumentPanel
            documentTemplate="Cahier_des_charges_RCD_Nominatives.docx"
            onClose={this.handleCloseSidePanels}
            onHandleSubmitFile={this.handleSubmitProjectSharedDocument}
            submitting={this.state.isSubmittingProjectSharedDocument}
            document={this.state.document}
            extensions={'.docx'}
          />
        }

        {this.state.isResendReasonPanelOpen &&
          <SidePanel
            big
            onClose={this.handleCloseResendReasonPanel}
          >
            <ResendReasonForm
              onClose={this.handleCloseResendReasonPanel}
              onSubmit={this.handleSubmitResendReason}
            />
          </SidePanel>
        }
      </div>
    );
  }
}

ProjectDetailsView.propTypes = {
  projectId: PropTypes.number.isRequired,
  interventionId: PropTypes.number.isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
    pathname: PropTypes.string.isRequired
  }).isRequired,
  organisation: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  actions: PropTypes.shape({
    addError: PropTypes.func.isRequired,
    addSuccess: PropTypes.func.isRequired
  }).isRequired,
  currentLocale: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired
};

function mapStateToProps(state, {match}) {
  return {
    organisation: state.organisation,
    currentLocale: state.i18n.locale,
    user: state.user,
    projectId: Number(match.params.projectId),
    interventionId: Number(match.params.interventionId)
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({addError, addSuccess}, dispatch)
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ProjectDetailsView));
